import { FormGroup } from '@angular/forms';

export default class Utils {
  /**
   * Flatten a multidimensional object
   *
   * For example:
   *   flattenObject({ a: 1, b: { c: 2 } })
   * Returns:
   *   { a: 1, c: 2}
   */
  static flattenObject(obj: any) {
    const flattened = {};

    Object.keys(obj).forEach((key) => {
      if (typeof obj[key] === 'object' && obj[key] !== null) {
        Object.assign(flattened, this.flattenObject(obj[key]));
      } else {
        flattened[key] = obj[key];
      }
    });

    return flattened;
  }

  static deepCopy(obj: any) {
    let copy;

    // Handle the 3 simple types, and null or undefined
    if (null == obj || 'object' !== typeof obj) {
      return obj;
    }
    // Handle Date
    if (obj instanceof Date) {
      copy = new Date();
      copy.setTime(obj.getTime());
      return copy;
    }

    // Handle Array
    if (obj instanceof Array) {
      copy = [];
      for (let i = 0, len = obj.length; i < len; i++) {
        copy[i] = this.deepCopy(obj[i]);
      }
      return copy;
    }

    // Handle Object
    if (obj instanceof Object) {
      copy = {};
      for (const attr in obj) {
        if (obj.hasOwnProperty(attr)) {
          copy[attr] = this.deepCopy(obj[attr]);
        }
      }
      return copy;
    }

    throw new Error('Unable to copy obj! Its type isnt supported.');
  }

  static deepEquals(x, y) {
    if (x === y) {
      return true; // if both x and y are null or undefined and exactly the same
    } else if (!(x instanceof Object) || !(y instanceof Object)) {
      return false; // if they are not strictly equal, they both need to be Objects
    } else if (x.constructor !== y.constructor) {
      // they must have the exact same prototype chain, the closest we can do is
      // test their constructor.
      return false;
    } else {
      for (const p in x) {
        // HACK to ignore the displayName in the check TODO.. make this mor generic
        if (p == 'displayName') {
          continue;
        }

        if (!x.hasOwnProperty(p)) {
          continue; // other properties were tested using x.constructor === y.constructor
        }
        if (!y.hasOwnProperty(p)) {
          return false; // allows to compare x[ p ] and y[ p ] when set to undefined
        }
        if (x[p] === y[p]) {
          continue; // if they have the same strict value or identity then they are equal
        }
        if (typeof x[p] !== 'object') {
          return false; // Numbers, Strings, Functions, Booleans must be strictly equal
        }
        if (!this.deepEquals(x[p], y[p])) {
          console.log("wasn'tequal because: " + p, x[p], y[p]);
          return false;
        }
      }
      for (const p in y) {
        if (y.hasOwnProperty(p) && !x.hasOwnProperty(p)) {
          console.log("wasn'tequal because y has : " + p);
          return false;
        }
      }
      return true;
    }
  }

  /**
   * Outputs a diff of (only) changed properties  to the console
   * in the format
   * Updated Properties:
   * - property1 - [oldvalue] ==> [newvalue]
   * - property2.nestedField1 - [oldvalue] ==> [newvalue]
   * ...
   * it will print all properties and iterate the object recursivly (only leafs will be printed)
   *
   */
  static logDiff(originalModel: any, updatedModel: any) {
    this.logChangedProperties(originalModel, updatedModel);
  }

  static logChangedProperties(originalObj: any, updatedObj: any, path: string = '') {
    for (const key in updatedObj) {
      if (updatedObj.hasOwnProperty(key)) {
        const updatedValue = updatedObj[key];
        const originalValue = originalObj[key];

        if (this.isObject(updatedValue) && this.isObject(originalValue)) {
          this.logChangedProperties(originalValue, updatedValue, `${path}${key}.`);
        } else if (updatedValue !== originalValue) {
          console.log(`- ${path}${key} - [${originalValue}] ==> [${updatedValue}]`);
        }
      }
    }
  }

  static isObject(obj: any) {
    return obj !== null && typeof obj === 'object';
  }
}
